import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";

import { AmountTypeEnum, IbanInvoiceUpdateCommand } from "api";
import { useAppDispatch } from "store/hooks";
import {
  IbanInvoice,
  updateIbanInvoiceByUid,
} from "store/features/ibanInvoiceSlice";

import { FormValues, validationSchema } from "./validation";

interface UseInvoiceUpdateFormArgs {
  invoice?: IbanInvoice | null;
  onAfterSubmit?: () => Promise<void>;
  onClose: () => void;
}

export const useInvoiceUpdateForm = ({
  invoice,
  onAfterSubmit,
  onClose,
}: UseInvoiceUpdateFormArgs) => {
  const dispatch = useAppDispatch();

  const methods = useForm<FormValues>({
    mode: "onSubmit",
    resolver: yupResolver(validationSchema),
    values: {
      amount: invoice?.amount || 0,
      amountType: invoice?.amountType || AmountTypeEnum.MerchantDefined,
      paymentPurpose: invoice?.paymentPurpose || "",
      showNotes: (invoice?.notes || "").length > 0,
      notes: invoice?.notes || "",
      showClientNotes: (invoice?.clientNotes || "").length > 0,
      clientNotes: invoice?.clientNotes || "",
      showInvoiceExpiration: !!invoice?.expirationDateTimeUtc,
      expirationDateTimeLocal: invoice?.expirationDateTimeUtc
        ? dayjs.utc(invoice?.expirationDateTimeUtc).local()
        : null,
      requestPayerFullName:
        invoice?.paymentPurposeOptions?.requestPayerFullName || false,
      requestOrderNumber:
        invoice?.paymentPurposeOptions?.requestOrderNumber || false,
    },
  });

  const prepareInvoicePayload = (
    values: FormValues,
    ibanInvoiceUid: string,
  ): IbanInvoiceUpdateCommand => {
    const {
      showNotes,
      showClientNotes,
      showInvoiceExpiration,
      expirationDateTimeLocal,
      requestPayerFullName,
      requestOrderNumber,
      ...rest
    } = values;

    const expirationDateTimeUtc =
      showInvoiceExpiration && expirationDateTimeLocal
        ? dayjs(expirationDateTimeLocal).toISOString()
        : undefined;

    const paymentPurposeOptions = {
      requestPayerFullName: requestPayerFullName || false,
      requestOrderNumber: requestOrderNumber || false,
    };

    return {
      ...rest,
      paymentPurposeOptions,
      expirationDateTimeUtc,
      notes: showNotes ? values.notes : "",
      clientNotes: showClientNotes ? values.clientNotes : "",
      ibanInvoiceUid,
    };
  };

  const onCloseModal = () => {
    onClose();
    methods.reset();
  };

  const onSubmit = async () => {
    await methods.handleSubmit(async (values: FormValues) => {
      if (!invoice?.ibanQrInvoiceUid) return;

      const body = prepareInvoicePayload(values, invoice.ibanQrInvoiceUid);

      await dispatch(updateIbanInvoiceByUid({ body })).unwrap();
      await onAfterSubmit?.();

      onCloseModal();
    })();
  };

  return { methods, onSubmit, onCloseModal };
};
